import { z } from 'zod'

export enum KnowledgeEntryType {
  TEXT = 'text',
  FILE = 'file',
  LINK = 'link',
}

export enum KnowledgeEntryState {
  PROCESSED = 'processed',
  IN_PROCESS = 'in_process',
  FAILED = 'failed',
}

export enum KnowledgeErrorCode {
  BLACK_LISTED_DOMAIN = 'blacklisted_domain',
  PARSING_ERROR = 'parsing_error',
  INTERNAL_ERROR = 'internal_error',
}

const baseEntrySchema = z.object({
  id: z.string(),
  type: z.enum(KnowledgeEntryType),
  title: z.string().optional(),
  state: z.enum(KnowledgeEntryState),
  error_code: z.enum(KnowledgeErrorCode).optional(),
})

const textEntrySchema = baseEntrySchema.extend({
  type: z.literal(KnowledgeEntryType.TEXT),
  data: z.object({
    text: z.string(),
  }),
})

const textEntryDraftSchema = textEntrySchema.omit({
  state: true,
  error_code: true,
})

const linkEntrySchema = baseEntrySchema.extend({
  type: z.literal(KnowledgeEntryType.LINK),
  data: z.object({
    url: z.string().url(),
  }),
})

const linkEntryDraftSchema = linkEntrySchema.omit({
  state: true,
  error_code: true,
})

const fileEntrySchema = baseEntrySchema.extend({
  type: z.literal(KnowledgeEntryType.FILE),
  data: z.object({
    file: z.string(),
  }),
})

const fileEntryDraftSchema = fileEntrySchema.omit({
  state: true,
  error_code: true,
})

const entrySchema = z.discriminatedUnion('type', [
  textEntrySchema,
  linkEntrySchema,
  fileEntrySchema,
])

const entryDraftSchema = z.discriminatedUnion('type', [
  textEntryDraftSchema,
  linkEntryDraftSchema,
  fileEntryDraftSchema,
])

export const AiBusinessKnowledgeApiSchemas = {
  get: {
    response: z.object({
      data: entrySchema,
    }),
    query: z.object({
      entry_id: z.string(),
    }),
    path: z.undefined(),
  },
  retrieve: {
    response: z.object({
      data: z.array(entrySchema),
      limiter: z.number().nullable(),
    }),
    query: z
      .object({
        limiter: z.number().optional(),
        limiter_type: z.string().optional(),
      })
      .optional(),
    path: z.undefined(),
  },
  create: {
    request: z.object({
      data: entryDraftSchema,
    }),
    response: z.object({
      data: entrySchema,
    }),
    query: z
      .object({
        client_id: z.string().optional(),
      })
      .optional(),
    path: z.undefined(),
  },
  updateData: {
    request: z.object({
      data: entrySchema,
    }),
    response: z.object({
      data: entrySchema,
    }),
    query: z
      .object({
        client_id: z.string().optional(),
      })
      .optional(),
    path: z.undefined(),
  },
  delete: {
    request: z.object({
      id: z.string().uuid(),
    }),
    response: z.undefined(),
    query: z
      .object({
        client_id: z.string().optional(),
      })
      .optional(),
    path: z.undefined(),
  },
}

export const AiBusinessKnowledgeSchemas = {
  entry: entrySchema,
}

export type KnowledgeEntry = z.infer<typeof entrySchema>
export type KnowledgeEntryDraft = z.infer<typeof entryDraftSchema>
export type TextEntry = z.infer<typeof textEntrySchema>
export type TextEntryDraft = z.infer<typeof textEntryDraftSchema>
export type LinkEntry = z.infer<typeof linkEntrySchema>
export type LinkEntryDraft = z.infer<typeof linkEntryDraftSchema>
